import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { conceptsForEntity, type CodexConcept } from "@/lib/codex";
import { Sparkles } from "lucide-react";

interface Props {
  entityKind: "gate" | "book" | "wisdom" | "activation" | "release" | "challenge";
  entityRef: string;
  heading?: string;
}

export function CodexRail({ entityKind, entityRef, heading = "Related in the Codex" }: Props) {
  const [concepts, setConcepts] = useState<CodexConcept[]>([]);

  useEffect(() => {
    let cancelled = false;
    conceptsForEntity(entityKind, entityRef)
      .then((list) => {
        if (!cancelled) setConcepts(list);
      })
      .catch(() => void 0);
    return () => {
      cancelled = true;
    };
  }, [entityKind, entityRef]);

  if (concepts.length === 0) return null;

  return (
    <section aria-label={heading} className="mt-12 rounded-2xl border hairline bg-obsidian/40 p-6">
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-gold" aria-hidden />
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{heading}</p>
      </div>
      <ul className="mt-4 flex flex-wrap gap-2">
        {concepts.map((c) => (
          <li key={c.slug}>
            <Link
              to="/codex/$slug"
              params={{ slug: c.slug }}
              className="inline-flex flex-col rounded-xl border hairline bg-obsidian/60 px-4 py-2 text-left transition-colors hover:border-gold hover:bg-obsidian/80"
            >
              <span className="font-serif text-sm text-gold-luminous">{c.title}</span>
              {c.essence && (
                <span className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                  {c.essence}
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
